import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableHead,
    TableRow,
    TableBody,
    CircularProgress,
    LinearProgress,
    Alert,
    Snackbar,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Breadcrumbs,
    Link,
    Typography
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import collectionService, { CollectionProduct } from '../../../shared/services/collectionService';
import { getProductNames } from '../../../shared/services/productService';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';

export const CollectionProducts: React.FC = () => {
    const { id: collectionId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();

    const [products, setProducts] = useState<CollectionProduct[]>([]);
    const [productNameMap, setProductNameMap] = useState<Record<number, string>>({});
    const [collectionName, setCollectionName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [openDialog, setOpenDialog] = useState(false);
    const [productIdInput, setProductIdInput] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load products on mount or when collectionId changes
    useEffect(() => {
        if (collectionId) {
            loadProducts();
        }
    }, [collectionId]);

    const loadProducts = async () => {
        if (!collectionId) return;
        try {
            setLoading(true);
            setError('');
            const response = await collectionService.listCollectionProducts(collectionId, {
                page: 1,
                limit: 1000
            });

            let items: CollectionProduct[] = [];
            const data = response.data;
            if (data && typeof data === 'object' && 'items' in data && Array.isArray(data.items)) {
                items = data.items;
            } else if (Array.isArray(data)) {
                items = data;
            }
            setProducts(items);

            // Fetch product names for all productIds
            if (items.length > 0) {
                try {
                    const namesResponse = await getProductNames();
                    if (namesResponse.success && Array.isArray(namesResponse.data)) {
                        const map: Record<number, string> = {};
                        namesResponse.data.forEach((p) => { map[p.productId] = p.name; });
                        setProductNameMap(map);
                    }
                } catch {
                    // names are optional — silently ignore
                }
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load products');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const paginatedProducts = useMemo(() => {
        const start = currentPage * pageSize;
        return products.slice(start, start + pageSize);
    }, [products, currentPage, pageSize]);

    const handleOpenDialog = () => {
        setProductIdInput('');
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setProductIdInput('');
    };

    const handleAddProduct = async () => {
        if (!collectionId) return;
        if (!productIdInput.trim()) {
            setError('Product ID is required');
            return;
        }

        try {
            setIsSubmitting(true);
            setError('');
            const productIds = productIdInput.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));

            if (productIds.length === 0) {
                setError('Please enter valid product IDs');
                return;
            }

            await collectionService.addProductToCollection(collectionId, {
                productId: productIds.length === 1 ? productIds[0] : productIds
            });

            setSuccess(`Product(s) added successfully`);
            handleCloseDialog();
            await loadProducts();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to add product');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveProduct = async (product: CollectionProduct) => {
        if (!collectionId) return;
        if (!window.confirm(`Are you sure you want to remove product ${product.productId}?`)) return;

        try {
            setError('');
            await collectionService.removeProductFromCollection(collectionId, product.productId);
            setSuccess('Product removed successfully');
            await loadProducts();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to remove product');
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const canAddProduct = isSuperAdmin() || hasPermission('collection.update');
    const canRemoveProduct = isSuperAdmin() || hasPermission('collection.delete');

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <div className="-ml-4 mb-2">
                        <RouterLink to="/collections" className="back-link">
                            <ArrowBackIcon className="text-[#0EA5E9]" />
                            <span className="ml-2 text-[#0EA5E9]">Back</span>
                        </RouterLink>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Collection Products
                    </h2>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading && products.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && products.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : products.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                No products in this collection. Click "Add Products" to add some.
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Product ID</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Product Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Added At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={products.length === 0}
                                emptyColSpan={4}
                                emptyMessage="No products found."
                            >
                                {paginatedProducts.map((product) => (
                                    <TableRow key={product.id} hover>
                                        <TableCell>
                                            <span className="font-medium">{product.productId}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span>{productNameMap[product.productId] ?? '-'}</span>
                                        </TableCell>
                                        <TableCell>
                                            {product.createdAt
                                                ? new Date(product.createdAt).toLocaleDateString()
                                                : '-'
                                            }
                                        </TableCell>
                                        <TableCell align="right">
                                            {canRemoveProduct && (
                                                <Tooltip title="Remove">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveProduct(product)}
                                                        sx={{ color: '#DC2626' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            {products.length > pageSize && (
                                <AppTablePagination
                                    totalRows={products.length}
                                    page={currentPage}
                                    pageSize={pageSize}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={(size) => {
                                        setPageSize(size);
                                        setCurrentPage(0);
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Add Products Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Add Products to Collection</DialogTitle>
                <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                        Enter product IDs separated by commas (e.g., 1, 2, 3)
                    </Typography>
                    <TextField
                        autoFocus
                        label="Product ID(s)"
                        name="productIds"
                        value={productIdInput}
                        onChange={(e) => setProductIdInput(e.target.value)}
                        fullWidth
                        required
                        placeholder="e.g., 1, 2, 3 or just 1"
                        multiline
                        rows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <AppButton
                        variant="secondary"
                        onClick={handleCloseDialog}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </AppButton>
                    <AppButton
                        variant="primary"
                        onClick={handleAddProduct}
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />}
                    >
                        {isSubmitting ? 'Adding...' : 'Add'}
                    </AppButton>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
